import axios from 'axios';

const PUBLIC_API_BASE_URL = process.env.REACT_APP_PUBLIC_API_URL!;

// Create axios instance for public APIs (no authentication by default)
export const publicApi = axios.create({
  baseURL: PUBLIC_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interfaces for public meeting API
export interface PublicMeetingParticipant {
  id: string;
  user_id: string;
  user_name: string;
  is_host: boolean;
  token: string;
}

export type MeetingType = 'instant' | 'scheduled';
export type WeekDay = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export interface PublicMeeting {
  id: string;
  title: string;
  description?: string;
  type: MeetingType;
  start_date?: string | null;
  start_time?: string | null;
  end_date?: string | null;
  end_time?: string | null;
  available_days?: WeekDay[] | null;
  is_recording_enabled: boolean;
  is_screen_sharing_enabled: boolean;
  is_chat_enabled: boolean;
  status: string;
}

export interface PublicMeetingJoinData {
  participant: PublicMeetingParticipant;
  meeting: PublicMeeting;
}

export interface JoinMeetingResponse {
  meeting_id: string;
  token: string;
  participant_identity: string;
  is_host: boolean;
  initial_media_states: {
    camera_enabled: boolean;
    microphone_enabled: boolean;
  };
  meeting: PublicMeeting;
  message?: string; // Add message property to handle "Already in meeting" response
}

// Public Meeting API (token-based access)
export const publicMeetingAPI = {
  // Get participant by token (for validation before joining)
  getParticipantByToken: (meetingId: string, token: string) =>
    publicApi.get<{ success: boolean; data: PublicMeetingJoinData }>(`/meetings/${meetingId}/join/${token}`),

  // Join meeting with token-based authentication
  joinMeeting: (
    meetingId: string,
    token: string,
    data: { camera_enabled?: boolean; microphone_enabled?: boolean }
  ) =>
    publicApi.post<{ success: boolean; data: JoinMeetingResponse; message: string }>(
      `/meetings/${meetingId}/join/${token}`,
      data
    ),

  // Update participant media state
  updateMediaState: (
    meetingId: string,
    token: string,
    data: { camera_enabled?: boolean; microphone_enabled?: boolean }
  ) =>
    publicApi.put<{ 
      success: boolean; 
      data: { camera_enabled: boolean; microphone_enabled: boolean }; 
      message: string 
    }>(`/meetings/${meetingId}/participant/${token}/media`, data),

  // Start recording (host only)
  startRecording: (
    meetingId: string,
    token: string,
    data?: { layout?: string; audio_only?: boolean; width?: number; height?: number; show_participant_labels?: boolean; custom_template?: string }
  ) =>
    publicApi.post<{ 
      success: boolean; 
      data: { 
        recording_id: string; 
        status: string; 
        started_at: string; 
        type: string; 
        file_name: string; 
      }; 
      message: string 
    }>(`/meetings/${meetingId}/participant/${token}/recording/start`, data || {}),

  // Stop recording (host only)
  stopRecording: (meetingId: string, token: string, recordingId: string) =>
    publicApi.post<{ 
      success: boolean; 
      data: { 
        recording_id: string; 
        status: string; 
        started_at: string; 
        ended_at: string; 
        type: string; 
        file_name: string; 
        file_size?: number; 
        duration?: number; 
        error?: string; 
      }; 
      message?: string 
    }>(`/meetings/${meetingId}/participant/${token}/recording/stop`, { recording_id: recordingId }),

  // Chat/Message API for public meetings
  getMessages: (meetingId: string, token: string, page = 1, limit = 50) =>
    publicApi.get<{
      success: boolean;
      data: {
        messages: Array<{
          id: string;
          content: string;
          type: 'text' | 'system';
          sent_at: string;
          participant: {
            id: string;
            user: {
              id: string;
              first_name: string;
              last_name: string;
            };
          };
        }>;
        total: number;
        page: number;
        limit: number;
      };
      message?: string;
    }>(`/meetings/${meetingId}/participant/${token}/messages?page=${page}&limit=${limit}`),

  sendMessage: (
    meetingId: string,
    token: string,
    data: { content: string; type?: 'text' | 'system' }
  ) =>
    publicApi.post<{
      success: boolean;
      data: {
        message: {
          id: string;
          content: string;
          type: 'text' | 'system';
          sent_at: string;
          participant: {
            id: string;
            user: {
              id: string;
              first_name: string;
              last_name: string;
            };
          };
        };
      };
      message?: string;
    }>(`/meetings/${meetingId}/participant/${token}/messages`, {
      content: data.content,
      type: data.type ?? 'text',
    }),

  deleteMessage: (meetingId: string, token: string, messageId: string) =>
    publicApi.delete<{
      success: boolean;
      message: string;
    }>(`/meetings/${meetingId}/participant/${token}/messages/${messageId}`),

  // End meeting (host only)
  endMeeting: (meetingId: string, token: string) =>
    publicApi.post<{
      success: boolean;
      data: {
        meeting_id: string;
        status: string;
        ended_at: string;
      };
      message: string;
    }>(`/meetings/${meetingId}/participant/${token}/end`),
};

// Public Meeting Creation API (requires app authentication)
export const publicMeetingCreationAPI = {
  // Create meeting (requires app credentials in headers)
  createMeeting: (
    data: {
      title: string;
      description?: string;
      is_recording_enabled?: boolean;
      is_screen_sharing_enabled?: boolean;
      is_chat_enabled?: boolean;
    },
    appId: string,
    appSecret: string
  ) =>
    axios.post<{
      success: boolean;
      data: {
        meeting_id: string;
        title: string;
        description?: string;
        is_recording_enabled: boolean;
        is_screen_sharing_enabled: boolean;
        is_chat_enabled: boolean;
        status: string;
        created_at: string;
      };
      message: string;
    }>(
      `${PUBLIC_API_BASE_URL}/meetings`,
      data,
      {
        headers: {
          'App-Id': appId,
          'App-Secret': appSecret,
          'Content-Type': 'application/json',
        },
      }
    ),

  // Add host participant (requires app credentials in headers)
  addHost: (
    meetingId: string,
    data: { user_id?: string; user_name: string },
    appId: string,
    appSecret: string
  ) =>
    axios.post<{
      success: boolean;
      data: {
        participant_id: string;
        user_id: string;
        user_name: string;
        is_host: boolean;
        token: string;
        join_link: string;
      };
      message: string;
    }>(
      `${PUBLIC_API_BASE_URL}/meetings/${meetingId}/host`,
      data,
      {
        headers: {
          'App-Id': appId,
          'App-Secret': appSecret,
          'Content-Type': 'application/json',
        },
      }
    ),

  // Add participant (public, no authentication required)
  addParticipant: (meetingId: string, data: { user_id?: string; user_name: string }) =>
    axios.post<{
      success: boolean;
      data: {
        participant_id: string;
        user_id: string;
        user_name: string;
        is_host: boolean;
        token: string;
        join_link: string;
      };
      message: string;
    }>(`${PUBLIC_API_BASE_URL}/meetings/${meetingId}/participant`, data),
};

export default publicApi;